import { Button, Chip, Stack, Typography } from "@mui/material";
import { PaperBox } from "../../../../../../components/Layout/PaperBox";
import { CardProps } from "..";
import DisplayNotFoundInfo from "../../../../../../components/DisplayNotFindInfo";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import TimeLineStepper from "../../../../../../components/BasicComponents/DisplayDados/TimeLineStepper";
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { useMemo, useState } from "react";
import DialogEmitirNotaFiscal from "./DialogEmitirNotaFiscal";
import { TableSimples } from "../../../../../../components/BasicComponents/DisplayDados/TableSimples";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import BtnEmitirNotaFiscal from "./BtnEmitirNotaFiscal";
import useImprimirArquivo from "../../../../../../lib/useImprimirArquivo";
import AlertReceituarioAgronomico from "../CardSituacaoPedido/StepExpedicao/AlertReceituarioAgronomico";
import { usePermission } from "../../../../../../components/PermissionGuard";
import DialogCancelarExpedicao from "./DialogCancelarExpedicao";

export default function CardHistoricoExpedicao({
    data
}: PropsCardHistoricoExpedicao) {

    const queryClient = useQueryClient();

    const hasPermission = usePermission("comercial.realizar-expedicao");
    const permiteCancelarExpedicao = usePermission("comercial.cancelar-expedicao-pedido");

    const imprimirArquivo = useImprimirArquivo()

    const [openEmitirNota, setOpenEmitirNota] = useState<number | null>(null);
    const [openCancelarExpedicao, setOpenCancelarExpedicao] = useState<number | null>(null);

    const receituariosEmitido = useMemo(() => {
        return data.itens.some(i => i.receita_id && i.emite_receita === "S")
    }, [data]);

    //queries e mutations
    const { mutate: concluirExpedicao, isLoading } = useMutation({
        mutationKey: 'concluir-expedicao-pedido',
        mutationFn: (input: number) => axiosClient.put(`/comercial/pedidos/concluir-expedicao/${input}`, {}),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao concluir expedição!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Expedição concluída com sucesso!`, { variant: 'success' });
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
        }
    })

    return (
        <PaperBox>
            <Typography variant="h6">Histórico de expedição</Typography>
            {data.situacao === "Finalizado" && receituariosEmitido && <AlertReceituarioAgronomico data={data} />}
            {data.historico_expedicao.length === 0 && (
                <DisplayNotFoundInfo label="Nenhuma expedição encontrada" />
            )}
            {
                data.historico_expedicao.length > 0 && (
                    <>
                        <TimeLineStepper
                            Icon={CheckCircleIcon}
                            dinamicIcon={(index) => {
                                const status = data.historico_expedicao[index].status;
                                return status === "Cancelada" ? CloseIcon : CheckCircleIcon;
                            }}
                            dinamicIconProps={(index) => {
                                const status = data.historico_expedicao[index].status;
                                return {
                                    color: status === "Cancelada" ? "error" : "primary"
                                };
                            }}
                            iconProps={{
                                fontSize: "medium",
                                color: "primary",
                                sx: { display: "flex", alignItems: "center", justifyContent: "center" }
                            }}
                            Itens={data.historico_expedicao.map((item) => {
                                return {
                                    itensLabel: (
                                        <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
                                            <Stack direction={'column'}>
                                                {
                                                    item.status === "Cancelada" ?
                                                        <>
                                                            <Typography variant="body1" color="textSecondary">
                                                                Cancelada por {item.cancelado_por} em {item.cancelado_em}
                                                            </Typography>
                                                            <Typography variant="caption" color="error">
                                                                Motivo: {item.motivo_cancelamento}
                                                            </Typography>
                                                        </>
                                                        : <>
                                                            <Typography variant="body1" color="textSecondary">
                                                                {item.criado_em} por {item.criado_por} - Loja: {item.loja.id} - {item.loja.nome}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Código CS9: {item.codigo_cs9}
                                                            </Typography>
                                                        </>
                                                }

                                            </Stack>
                                            {
                                                <Chip label={getItemChipStatus(item.status).label} variant="outlined" color={getItemChipStatus(item.status).color} />
                                            }
                                        </Stack>
                                    ),
                                    itensContent: (
                                        <>
                                            <TableSimples
                                                headers={[
                                                    { label: "Produto" },
                                                    { label: "Quantidade" },
                                                    { label: "Preço", numeric: true },
                                                ]}
                                                rows={item.itens.map(row => ({
                                                    columns: [
                                                        { value: `${row.produto_id} - ${row.produto_nome}` },
                                                        { value: row.quantidade_expedida },
                                                        { value: item.status === "Cancelada" ? '-' : formatarValorMonetario(row.preco), numeric: true },
                                                    ]
                                                }))}
                                                total={{
                                                    label: "Total",
                                                    value: item.status === "Cancelada" ? '-' : formatarValorMonetario(item.itens.reduce((total, row) => total + (row.preco), 0))
                                                }}
                                            />

                                            <Stack direction={'row'} mt={2} spacing={2} width="100%" justifyContent="flex-end">
                                                {
                                                    item.status !== "Cancelada" &&
                                                    <>
                                                        {
                                                            !item.status_nf && permiteCancelarExpedicao &&
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() => setOpenCancelarExpedicao(item.id)}
                                                            >
                                                                Cancelar expedição
                                                            </Button>
                                                        }
                                                        {
                                                            item.status !== "Concluída" &&
                                                            hasPermission &&
                                                            (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onClick={() => concluirExpedicao(item.id)}
                                                                    disabled={isLoading}
                                                                >
                                                                    Concluir separação
                                                                </Button>
                                                            )
                                                        }
                                                        {
                                                            !item.status_nf &&
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                disabled={item.status !== "Concluída"}
                                                                onClick={() => setOpenEmitirNota(item.id)}
                                                            >
                                                                Emitir nota fiscal
                                                            </Button>
                                                        }
                                                        {
                                                            item.status_nf && item.status_nf !== "Nota fiscal emitida" &&
                                                            <BtnEmitirNotaFiscal
                                                                expedicao={item}
                                                            />
                                                        }
                                                        {
                                                            item.status_nf && item.status_nf === "Nota fiscal emitida" &&
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={() => imprimirArquivo(`comercial/pedidos/imprimir-danfe/${item.id}`)}
                                                                disabled={item.status !== "Concluída"}
                                                                startIcon={<LocalPrintshopOutlinedIcon fontSize="small" />}
                                                            >
                                                                Imprimir DANFE
                                                            </Button>
                                                        }
                                                    </>
                                                }

                                            </Stack>

                                        </>
                                    )
                                }
                            })}
                        />
                    </>
                )}
            {
                Boolean(openEmitirNota) && openEmitirNota &&
                <DialogEmitirNotaFiscal
                    {...{ data }}
                    open={Boolean(openEmitirNota)}
                    handleClose={() => setOpenEmitirNota(null)}
                    expedicao_id={openEmitirNota}
                />
            }
            {
                openCancelarExpedicao !== null &&
                <DialogCancelarExpedicao
                    open={Boolean(openCancelarExpedicao)}
                    handleClose={() => setOpenCancelarExpedicao(null)}
                    expedicao_id={openCancelarExpedicao}
                />
            }
        </PaperBox>
    )
}

interface PropsCardHistoricoExpedicao extends CardProps { }

function getItemChipStatus(status: string): { label: string; color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } {
    switch (status) {
        case "Concluída":
            return { label: "Concluída", color: "primary" };
        case "Em separação":
            return { label: "Em separação", color: "info" };
        case "Cancelada":
            return { label: "Cancelada", color: "error" };
        default:
            return { label: status, color: "default" };
    }
}